import { upflowyStoreRefs, useUpflowyStore } from '@upflowy/firestore';
import { ConsumerAnalyticsService, FlowStatus, logger } from '@upflowy/logger';
import { FlowDoc } from '@upflowy/types';
import { collection, limit, query, where } from 'firebase/firestore';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useApp } from './app-provider';
import { useNetworkErrorHandler } from './network-error-handler-provider';

import Loading from '../components/Loading';
import NotFound from '../components/NotFound';
import { fetchDoc, fetchDocs } from '../helpers/fetch-documents';
import { useNetworkState } from '../hooks/useNetworkState';
import { useRedirect } from '../hooks/useRedirect';
import { getFlowConfig } from '../services/callable-functions';

type FlowConfig = {
  showWatermark: boolean;
};

const defaultConfig: FlowConfig = {
  showWatermark: false,
};

export const FlowConfigContext = React.createContext<
  FlowConfig & {
    organisationId: string | null;
    flowId: string | null;
    flow: FlowDoc | null;
  }
>({
  organisationId: null,
  flowId: null,
  flow: null,
  ...defaultConfig,
});

export const useFlowConfig = () => useContext(FlowConfigContext);

export const DEFAULT_MAX_RETRY_COUNT = 3; // 3 retries

export const FlowConfigProvider = ({ children }: { children: ReactNode }) => {
  const firestore = useUpflowyStore();
  const { user, notFoundURL } = useApp();
  const handleRedirect = useRedirect();
  const { customURL, flowId }: Record<string, string | undefined> = useParams();
  const [notFound, setNotFound] = useState(false);
  const [flow, setFlow] = useState<FlowDoc | null>(null);
  const [networkError, setNetworkError] = useState(false);
  const [flowConfig, setFlowConfig] = useState<FlowConfig>();
  const { retryCount, networkErrorHandler } = useNetworkErrorHandler();
  const [retryFetchingFlowIndex, setRetryFetchingFlowIndex] = useState(0);
  const { isOffline } = useNetworkState();

  const retrievedFlowId = useMemo(() => flow?.id, [flow]);
  const retrievedFlowConfig = useMemo(
    () => flowConfig && 'showWatermark' in flowConfig,
    [flowConfig],
  );

  useEffect(() => {
    if (customURL) {
      const nanoId = customURL.substring(0, customURL.indexOf('-'));
      (async () => {
        try {
          if (retryFetchingFlowIndex < DEFAULT_MAX_RETRY_COUNT) {
            const flowSnapshots = await fetchDocs(
              query(collection(firestore, 'Flows'), where('nanoId', '==', nanoId), limit(1)),
            );

            if (flowSnapshots && flowSnapshots.empty) {
              if (retryFetchingFlowIndex === DEFAULT_MAX_RETRY_COUNT - 1) {
                setNotFound(true);
              }

              setRetryFetchingFlowIndex(retryFetchingFlowIndex + 1);
            } else {
              const flowDoc = flowSnapshots.docs[0].data() as FlowDoc;
              setFlow(flowDoc);

              logger.setTags({
                'organisation.id': flowDoc.organisationId,
              });
            }
          }
        } catch (error) {
          networkErrorHandler(error, () => setNetworkError(!retrievedFlowId));
        }
      })();
    }
  }, [
    retryFetchingFlowIndex,
    customURL,
    firestore,
    retryCount,
    networkErrorHandler,
    retrievedFlowId,
  ]);

  useEffect(() => {
    if (flowId) {
      const { flows } = upflowyStoreRefs(firestore);
      (async () => {
        try {
          const flowRef = flows().id(flowId).ref;
          const ref = await fetchDoc(flowRef);
          const flowDoc = ref.data() as FlowDoc;

          if (!flowDoc) {
            setNotFound(true);
          } else {
            setFlow(flowDoc);
            logger.setTags({
              'organisation.id': flowDoc.organisationId,
            });
          }
        } catch (error) {
          networkErrorHandler(error, () => setNetworkError(!retrievedFlowId));
        }
      })();
    }
  }, [flowId, firestore, retryCount, networkErrorHandler, retrievedFlowId]);

  useEffect(() => {
    if (flow?.id && user?.idToken) {
      getFlowConfig({
        flowId: flow.id,
        idToken: user.idToken,
      })
        .then(({ data: config }) => {
          setFlowConfig(config);
        })
        .catch((err) => {
          networkErrorHandler(err, () => setNetworkError(!retrievedFlowConfig));
        });
    }
  }, [flow?.id, user?.idToken, retryCount, networkErrorHandler, retrievedFlowConfig]);

  useEffect(() => {
    if (notFound && !flowConfig) {
      logger.logException(new Error('Loading finish with flow not found'));
      ConsumerAnalyticsService.endTransaction(FlowStatus.NOT_FOUND);
    }
  }, [notFound, flowConfig]);

  const value = {
    organisationId: flow?.organisationId ?? null,
    flowId: flow?.id ?? null,
    flow,
    showWatermark: false,
    ...flowConfig,
  };

  logger.setContext('FlowConfigProvider', value);

  if (notFound) {
    logger.warn('Flow Not Found', {
      customURL,
      flowId,
      retryCount,
      uid: `${user && user.uid}`,
    });
  }

  if (notFound && notFoundURL) {
    handleRedirect(notFoundURL);
  }

  if (networkError && !window.location.pathname.startsWith('/offline')) {
    handleRedirect(`/offline?returnTo=${window.location.pathname}`);
  }

  return (
    <div>
      {!notFound && !flow && !flowConfig && <Loading />}
      {notFound && !notFoundURL && !flow && !flowConfig && !isOffline && <NotFound />}
      {flow && flowConfig && (
        <FlowConfigContext.Provider value={value}>{children}</FlowConfigContext.Provider>
      )}
    </div>
  );
};
